import { getCompanyHeadCountId, getCompanyTypeId } from "./companyIds";
import { getYearsAtId } from "./dateIds";
import { getRegionId } from "./geographyIds";
import { getIndustryId } from "./industry";
import { getJobTitleId } from "./linkedinJobTitleIds";
import { getSchoolId } from "./linkedinSchoolIds";
import { getProfileLanguageId } from "./personaIds";
import { getRoleFunctionId, getSeniorityLevelId } from "./roleIds";

export interface SalesNavigatorSearchCriteria {
  // Basic Search
  keywords?: string;
  firstName?: string;
  lastName?: string;
  
  // Region
  geography?: MultiSelectItem[];
  
  // Current Company - now supports multi-select with include/exclude
  currentCompany?: MultiSelectItem[];
  pastCompany?: MultiSelectItem[];
  companyType?: MultiSelectItem[];
  companyHeadquartersLocation?: MultiSelectItem[];
  
  // Title
  currentJobTitle?: MultiSelectItem[];
  pastJobTitle?: MultiSelectItem[];
  function?: MultiSelectItem[]; // Sales Navigator calls this "Function" 
  
  // School
  school?: MultiSelectItem[];
  
  // Profile Language
  profileLanguage?: MultiSelectItem[];
  
  // Industry - updated to support include/exclude
  industry?: MultiSelectItem[];
  
  // Company Headcount
  companyHeadcount?: MultiSelectItem[];
  
  // Years in Current Position
  yearsInCurrentPosition?: MultiSelectItem[];
  
  // Years at Current Company
  yearsAtCurrentCompany?: MultiSelectItem[];
  
  // Years of Experience
  yearsOfExperience?: MultiSelectItem[];
  
  // Seniority Level
  seniorityLevel?: MultiSelectItem[];
  
  // Connection Level & Best Path In
  connectionLevel?: ('F' | 'S' | 'O')[];
  connectionsOf?: string; // For "Connections of" filter
  
  // Groups
  groups?: MultiSelectItem[];
  
  // Sales Navigator Specific Filters
  
  // Buyer Intent
  followingYourCompany?: boolean;
  viewedYourProfileRecently?: boolean;
  
  // Best Path In
  pastColleague?: boolean;
  sharedExperiences?: boolean;
  
  // Recent Updates  
  changedJobs?: boolean;
  postedOnLinkedIn?: boolean;
}

interface FilterValue {
  text: string;
  selectionType: 'INCLUDED' | 'EXCLUDED';
  id?: string;
}
interface Filter {
  type: string;
  values: FilterValue[];
}

export interface MultiSelectItem {
  value: string
  label: string
  include: boolean // true for include, false for exclude
}

function buildFilterValue(item: MultiSelectItem, resolveId?: (text: string) => string | undefined): FilterValue {
  const v: FilterValue = {
    text: item.value,
    selectionType: item.include ? 'INCLUDED' : 'EXCLUDED'
  };
  const id = resolveId?.(item.value);
  if (id) v.id = id;
  return v;
}

function buildFilter(
  type: string,
  items: MultiSelectItem[] = [],
  resolveId?: (text: string) => string | undefined
): Filter | null {
  if (!items.length) return null;
  return { type, values: items.map((i) => buildFilterValue(i, resolveId)) };
}

function generateSessionId(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let s = '';
  for (let i = 0; i < 22; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
  return s + '==';
}

export function buildSalesNavigatorUrl(criteria: SalesNavigatorSearchCriteria): string {
  const baseUrl = 'https://www.linkedin.com/sales/search/people';
  const filters: Filter[] = [];

  const currentCompany = buildFilter('CURRENT_COMPANY', criteria.currentCompany);
  if (currentCompany) filters.push(currentCompany);

  const pastCompany = buildFilter('PAST_COMPANY', criteria.pastCompany);
  if (pastCompany) filters.push(pastCompany);

  const currentTitle = buildFilter('CURRENT_TITLE', criteria.currentJobTitle, getJobTitleId);
  if (currentTitle) filters.push(currentTitle);

  const pastTitle = buildFilter('PAST_TITLE', criteria.pastJobTitle, getJobTitleId);
  if (pastTitle) filters.push(pastTitle);

  const companyType = buildFilter('COMPANY_TYPE', criteria.companyType, getCompanyTypeId);
  if (companyType) filters.push(companyType);

  const companyHeadCount = buildFilter('COMPANY_HEADCOUNT', criteria.companyHeadcount, getCompanyHeadCountId);

  if (companyHeadCount) filters.push(companyHeadCount);

  const companyLocation = buildFilter('COMPANY_HEADQUARTERS', criteria.companyHeadquartersLocation, getRegionId);
  if (companyLocation) filters.push(companyLocation);

  const roleFunction = buildFilter('FUNCTION', criteria.function, getRoleFunctionId);
  if (roleFunction) filters.push(roleFunction);

  const seniority = buildFilter('SENIORITY_LEVEL', criteria.seniorityLevel, getSeniorityLevelId);
  if (seniority) filters.push(seniority);

  const yearsAtCurrentCompany = buildFilter('YEARS_AT_CURRENT_COMPANY', criteria.yearsAtCurrentCompany, getYearsAtId);
  if (yearsAtCurrentCompany) filters.push(yearsAtCurrentCompany);

  const yearsInCurrentPosition = buildFilter(
    'YEARS_IN_CURRENT_POSITION',
    criteria.yearsInCurrentPosition,
    getYearsAtId
  );
  if (yearsInCurrentPosition) filters.push(yearsInCurrentPosition);

  const currentRegion = buildFilter('REGION', criteria.geography, getRegionId);
  if (currentRegion) filters.push(currentRegion);

  const industry = buildFilter('INDUSTRY', criteria.industry, getIndustryId);
  if (industry) filters.push(industry);

  const profileLanguage = buildFilter('PROFILE_LANGUAGE', criteria.profileLanguage, getProfileLanguageId);
  if (profileLanguage) filters.push(profileLanguage);

  const yearsOfExperience = buildFilter('YEARS_OF_EXPERIENCE', criteria.yearsOfExperience, getYearsAtId);
  if (yearsOfExperience) filters.push(yearsOfExperience);

  const group = buildFilter('GROUP', criteria.groups);
  if (group) filters.push(group);

  const school = buildFilter('SCHOOL', criteria.school, getSchoolId);
  if (school) filters.push(school);

  // LinkedIn-ish query object → custom “(key:value)” format
  const recentSearchParam = `(id:${Math.floor(Math.random() * 10_000_000_000)},doLogHistory:true)`;

  let queryStr = `(recentSearchParam:${recentSearchParam}`;
  if (filters.length) {
    const filtersList = filters
      .map((f) => {
        const values = f.values
          .map((v) => {
            // include ID when present
            const parts = [
              v.id ? `id:${v.id}` : null,
              `text:${encodeURIComponent(v.text)}`,
              `selectionType:${v.selectionType}`
            ].filter(Boolean);
            return `(${parts.join(',')})`;
          })
          .join(',');
        return `(type:${f.type},values:List(${values}))`;
      })
      .join(',');
    queryStr += `,filters:List(${filtersList})`;
  }
  queryStr += `)`;

  const sessionId = generateSessionId();
  return `${baseUrl}?query=${queryStr}&sessionId=${sessionId}&viewAllFilters=true`;
}
